/**
 * Registration validation.
 *
 * Pure functions, so every rule is testable without a network or a provider.
 * The provider validates again on the server — this layer exists to give the
 * student an answer immediately, not to be the security boundary.
 */

import type { AgeBand } from '@yuan-math/learning';
import { AuthError, type RegistrationInput } from './types.ts';

/**
 * Age bands that require a guardian (§29).
 *
 * The platform never asks a child for a date of birth, so consent is keyed off
 * the band they chose. Erring towards requiring consent is the safe direction.
 */
export const CHILD_AGE_BANDS: AgeBand[] = ['kindergarten', 'elementary', 'middle'];

export const isChildBand = (band: AgeBand): boolean => CHILD_AGE_BANDS.includes(band);

/**
 * Deliberately permissive. Over-strict email regexes reject valid addresses,
 * and the only real proof an address works is that activation reaches it.
 */
export function isValidEmail(email: string): boolean {
  const trimmed = email.trim();
  if (trimmed.length < 3 || trimmed.length > 254) return false;
  if (/\s/.test(trimmed)) return false;
  const at = trimmed.indexOf('@');
  if (at <= 0 || at !== trimmed.lastIndexOf('@')) return false;
  const domain = trimmed.slice(at + 1);
  if (domain.length < 3 || !domain.includes('.')) return false;
  if (domain.startsWith('.') || domain.endsWith('.') || domain.includes('..')) return false;
  return true;
}

export const normalizeEmail = (email: string): string => email.trim().toLowerCase();

export type PasswordStrength = {
  /** 0–4. Below `MIN_PASSWORD_SCORE` the password is rejected. */
  score: number;
  /** Concrete, actionable, and never scolding. */
  suggestions: string[];
  acceptable: boolean;
};

export const MIN_PASSWORD_LENGTH = 10;
export const MIN_PASSWORD_SCORE = 2;

/**
 * Scores a password by what actually resists guessing: length first, variety
 * second. There is no forced-symbol rule, because those produce `Passw0rd!`
 * and little else.
 */
export function scorePassword(password: string): PasswordStrength {
  const suggestions: string[] = [];
  let score = 0;

  if (password.length >= MIN_PASSWORD_LENGTH) score += 1;
  if (password.length >= 14) score += 1;
  else suggestions.push('A longer password is the single best improvement — try a short phrase.');

  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/].filter((re) => re.test(password)).length;
  if (classes >= 2) score += 1;
  if (classes >= 3) score += 1;
  else if (password.length < 16) suggestions.push('Mixing in a capital letter or a number helps.');

  if (COMMON_PASSWORDS.has(password.toLowerCase())) {
    return { score: 0, suggestions: ['That is one of the most commonly used passwords. Please choose another.'], acceptable: false };
  }
  if (/^(.)\1+$/.test(password)) {
    return { score: 0, suggestions: ['A single repeated character is not a password.'], acceptable: false };
  }
  if (password.length < MIN_PASSWORD_LENGTH) {
    return { score: 0, suggestions: [`Use at least ${MIN_PASSWORD_LENGTH} characters.`], acceptable: false };
  }

  return { score: Math.min(4, score), suggestions, acceptable: score >= MIN_PASSWORD_SCORE };
}

const COMMON_PASSWORDS = new Set([
  'password', 'password1', 'password123', '12345678', '123456789', '1234567890',
  'qwertyuiop', 'letmein123', 'welcome123', 'iloveyou1', 'admin12345', 'football123',
  'yuanmath', 'mathematics', 'password12',
]);

export const MAX_DISPLAY_NAME_LENGTH = 40;

export function isValidDisplayName(name: string): boolean {
  const trimmed = name.trim();
  return trimmed.length >= 1 && trimmed.length <= MAX_DISPLAY_NAME_LENGTH;
}

/**
 * Validates a whole registration, throwing the first problem found with the
 * field it belongs to so the form can attach it precisely.
 */
export function validateRegistration(input: RegistrationInput): void {
  if (!isValidEmail(input.email)) {
    throw new AuthError('invalid-email', 'That does not look like an email address.', 'email');
  }
  if (!isValidDisplayName(input.displayName)) {
    throw new AuthError(
      'invalid-name',
      `Choose a name between 1 and ${MAX_DISPLAY_NAME_LENGTH} characters. A nickname is fine.`,
      'displayName',
    );
  }

  const strength = scorePassword(input.password);
  if (!strength.acceptable) {
    throw new AuthError('weak-password', strength.suggestions[0] ?? 'Please choose a stronger password.', 'password');
  }

  if (!input.acceptedTerms) {
    throw new AuthError('terms-not-accepted', 'Please accept the terms and the privacy notice to continue.', 'terms');
  }

  // Child accounts need a guardian, and it must be a different address —
  // otherwise "consent" is the child clicking their own link (§29).
  if (isChildBand(input.ageBand)) {
    if (!input.guardianEmail || !isValidEmail(input.guardianEmail)) {
      throw new AuthError(
        'guardian-required',
        'A parent or guardian’s email is needed for a learner of this age.',
        'guardianEmail',
      );
    }
    if (normalizeEmail(input.guardianEmail) === normalizeEmail(input.email)) {
      throw new AuthError(
        'guardian-same-as-child',
        'The guardian’s email must be different from the learner’s.',
        'guardianEmail',
      );
    }
  }
}

/** The account kind implied by the chosen age band. */
export function kindForAgeBand(band: AgeBand): 'self' | 'child' {
  return isChildBand(band) ? 'child' : 'self';
}
